import { useEffect, useState } from "react";
import { api } from "../api/client";
import { t } from "../i18n/en";
import DeviceList from "../components/DeviceList";
import { isIos, usePush } from "../push/usePush";
import { useSettings } from "../settings/useSettings";

function HomeAnchor() {
  const { settings, patch } = useSettings();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function useCurrentLocation() {
    setBusy(true);
    setError("");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await patch({ home_lat: pos.coords.latitude, home_lon: pos.coords.longitude });
        } catch (err) {
          setError(err instanceof Error ? err.message : String(err));
        } finally {
          setBusy(false);
        }
      },
      () => {
        setError(t.settings.locationFailed);
        setBusy(false);
      },
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  }

  if (!settings) return null;
  const hasHome = settings.home_lat !== null && settings.home_lon !== null;

  return (
    <section className="panel">
      <h2>{t.settings.homeTitle}</h2>
      <p className="hint">{t.settings.homeWhy}</p>

      {hasHome ? (
        <p className="value mono">
          {settings.home_lat!.toFixed(4)}, {settings.home_lon!.toFixed(4)}
        </p>
      ) : (
        <p className="warn">{t.settings.noHome}</p>
      )}

      {error && <p className="error">{error}</p>}

      <div className="row">
        <button onClick={() => void useCurrentLocation()} disabled={busy}>
          {busy ? t.settings.locating : t.settings.useCurrentLocation}
        </button>
        {hasHome && (
          <button className="secondary" onClick={() => void patch({ clear_home: true })} disabled={busy}>
            {t.settings.clearHome}
          </button>
        )}
      </div>
    </section>
  );
}

function QuietHours() {
  const { settings, patch } = useSettings();
  if (!settings) return null;

  const set = (key: "quiet_hours_start" | "quiet_hours_end", value: string) =>
    void patch({ [key]: value || null } as never);

  return (
    <section className="panel">
      <h2>{t.settings.quietTitle}</h2>
      <p className="hint">{t.settings.quietWhy}</p>
      <div className="row">
        <div>
          <label htmlFor="quiet-start">{t.settings.from}</label>
          <input
            id="quiet-start"
            type="time"
            value={settings.quiet_hours_start?.slice(0, 5) ?? ""}
            onChange={(e) => set("quiet_hours_start", e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="quiet-end">{t.settings.to}</label>
          <input
            id="quiet-end"
            type="time"
            value={settings.quiet_hours_end?.slice(0, 5) ?? ""}
            onChange={(e) => set("quiet_hours_end", e.target.value)}
          />
        </div>
      </div>
      {(settings.quiet_hours_start || settings.quiet_hours_end) && (
        <button className="secondary" onClick={() => void patch({ clear_quiet_hours: true })}>
          {t.settings.clearQuiet}
        </button>
      )}
    </section>
  );
}

function UnitsSetting() {
  const { settings, patch } = useSettings();
  if (!settings) return null;
  return (
    <section className="panel">
      <h2>{t.settings.unitsTitle}</h2>
      <select
        aria-label={t.settings.unitsTitle}
        value={settings.units}
        onChange={(e) => void patch({ units: e.target.value as "aviation" | "metric" })}
      >
        <option value="metric">{t.settings.unitsMetric}</option>
        <option value="aviation">{t.settings.unitsAviation}</option>
      </select>
      <p className="hint">{t.settings.unitsHint}</p>
    </section>
  );
}

function RepeatTypes() {
  const { settings, patch } = useSettings();
  if (!settings) return null;

  return (
    <section className="panel">
      <h2>{t.settings.fatigueTitle}</h2>
      <p className="hint">{t.settings.fatigueWhy}</p>

      <label htmlFor="threshold">
        {t.settings.threshold} — <strong>{settings.auto_mute_threshold} {t.settings.thresholdUnit}</strong>
      </label>
      <input
        id="threshold"
        type="range"
        min={0}
        max={200}
        step={5}
        value={settings.auto_mute_threshold}
        onChange={(e) => void patch({ auto_mute_threshold: Number(e.target.value) })}
      />

      <label htmlFor="fatigue-mode">{t.settings.fatigueMode}</label>
      <select
        id="fatigue-mode"
        value={settings.auto_mute_mode}
        onChange={(e) => void patch({ auto_mute_mode: e.target.value as "suggest" | "enforce" })}
      >
        <option value="suggest">{t.settings.fatigueSuggest}</option>
        <option value="enforce">{t.settings.fatigueEnforce}</option>
      </select>
      <p className="hint">{t.settings.fatigueModeHint}</p>
    </section>
  );
}

function Notifications() {
  const { status, busy, message, enable, disable, sendTest } = usePush();

  return (
    <section className="panel">
      <h2>{t.settings.notificationsTitle}</h2>

      {status === "needs-install" && (
        <>
          <p className="warn">{t.settings.iosInstallRequired}</p>
          <ol className="steps">
            {t.settings.iosSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </>
      )}

      {status === "unsupported" && (
        <p className="warn">{isIos() ? t.settings.iosInstallRequired : t.settings.pushUnsupported}</p>
      )}

      {status === "unconfigured" && <p className="warn">{t.settings.pushUnconfigured}</p>}

      {status === "denied" && <p className="warn">{t.settings.pushDenied}</p>}

      {status === "prompt" && (
        <>
          <p className="hint">{t.settings.pushWhy}</p>
          <button onClick={() => void enable()} disabled={busy}>
            {busy ? t.common.working : t.settings.enableNotifications}
          </button>
        </>
      )}

      {status === "subscribed" && (
        <>
          <p className="ok">{t.settings.pushOn}</p>
          <div className="row">
            {/* Not a convenience: on iOS, delivery is throttled and silent failure is the norm,
                so "did that work?" needs an answer you can produce on demand. */}
            <button onClick={() => void sendTest()} disabled={busy}>
              {busy ? t.common.working : t.settings.sendTest}
            </button>
            <button className="secondary" onClick={() => void disable()} disabled={busy}>
              {t.settings.disableNotifications}
            </button>
          </div>
        </>
      )}

      {message && <p className="hint">{message}</p>}

      {status !== "checking" && <DeviceList onRemoveCurrent={disable} />}
    </section>
  );
}

function useVersion() {
  const [version, setVersion] = useState<string | null>(null);
  useEffect(() => {
    api
      .get<{ version: string; git_sha: string }>("/health")
      .then((h) => setVersion(`${h.version} (${h.git_sha})`))
      .catch(() => setVersion(null));
  }, []);
  return version;
}

export default function Settings({ username, onSignOut }: { username: string; onSignOut: () => void }) {
  const version = useVersion();
  return (
    <div className="page">
      <h1>{t.settings.title}</h1>
      <HomeAnchor />
      <UnitsSetting />
      <QuietHours />
      <RepeatTypes />
      <Notifications />
      <section className="panel">
        <h2>{t.settings.accountTitle}</h2>
        <p className="dim">
          {t.settings.signedInAs} <strong>{username}</strong>
        </p>
        <button className="secondary" onClick={onSignOut}>
          {t.radar.signOut}
        </button>
        {version && (
          <p className="hint mono version-line">
            {t.settings.version} {version}
          </p>
        )}
      </section>
    </div>
  );
}
